import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import {
  FaArrowLeft, FaCalendarAlt, FaCheckCircle, FaCog, FaLayerGroup,
  FaPlus, FaSignal, FaTrash, FaUser, FaUsers
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';

const LearningJourneyEnrollments = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enrollmentToRemove, setEnrollmentToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [form, setForm] = useState({
    enrollment_type: 'user',
    user_ids: [],
    group_id: '',
    organization_id: '',
    expires_at: ''
  });
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [orgs, setOrgs] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [jRes, eRes] = await Promise.all([
        api.learningJourneys.getById(id),
        api.learningJourneys.getEnrollments(id)
      ]);
      setJourney(jRes.data?.data);
      setEnrollments(eRes.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const loadMasters = async () => {
      try {
        const [uRes, gRes, oRes] = await Promise.allSettled([
          api.users.getAll(),
          api.groups.list(),
          api.organizations.getAll()
        ]);
        if (uRes.status === 'fulfilled') {
          const raw = uRes.value.data?.data || uRes.value.data || [];
          setUsers(Array.isArray(raw) ? raw.filter((u) => Number(u.role) === 1) : []);
        }
        if (gRes.status === 'fulfilled') {
          const raw = gRes.value.data?.data || gRes.value.data || [];
          setGroups(Array.isArray(raw) ? raw : []);
        }
        if (oRes.status === 'fulfilled') {
          const raw = oRes.value.data?.data || oRes.value.data || [];
          setOrgs(Array.isArray(raw) ? raw : []);
        }
      } catch {
        // ignore
      }
    };
    loadMasters();
  }, []);

  const handleEnroll = async () => {
    try {
      setSaving(true);
      const payload = {
        enrollment_type: form.enrollment_type,
        expires_at: form.expires_at || null
      };
      if (form.enrollment_type === 'user') {
        payload.user_ids = form.user_ids.map(Number);
      } else if (form.enrollment_type === 'group') {
        payload.group_id = Number(form.group_id);
      } else {
        payload.organization_id = Number(form.organization_id);
      }
      await api.learningJourneys.enroll(id, payload);
      toast.success('Learners enrolled');
      setShowModal(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Enrollment failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!enrollmentToRemove) return;
    try {
      setRemoving(true);
      await api.learningJourneys.removeEnrollment(id, enrollmentToRemove.id);
      toast.success('Enrollment removed');
      setEnrollmentToRemove(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove enrollment');
    } finally {
      setRemoving(false);
    }
  };

  const toggleUser = (userId) => {
    setForm((prev) => ({
      ...prev,
      user_ids: prev.user_ids.includes(userId)
        ? prev.user_ids.filter((u) => u !== userId)
        : [...prev.user_ids, userId]
    }));
  };

  const canEnroll = form.enrollment_type === 'user'
    ? form.user_ids.length > 0
    : form.enrollment_type === 'group'
      ? Boolean(form.group_id)
      : Boolean(form.organization_id);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <Spinner animation="border" role="status" className="h-12 w-12 text-purple-600">
          <span className="visually-hidden">Loading journey enrollments...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-3 md:p-6 lg:p-8">
      <div className="mb-4">
        <Row className="align-items-center g-3">
          <Col md={2}>
            <Button
              bsPrefix="custom-hamburger"
              onClick={() => navigate('/admin/learning-journeys')}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-purple-300 bg-white px-4 py-2 text-sm font-semibold text-purple-600 shadow-md transition-all duration-200 hover:bg-purple-50 hover:shadow-lg"
              aria-label="Back to learning journeys"
            >
              <FaArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
            </Button>
          </Col>
          <Col md={7}>
            <h5 className="m-0 text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 md:text-3xl">
              Journey Enrollments
            </h5>
            <p className="mb-0 mt-1 text-sm text-gray-600">{journey?.name}</p>
          </Col>
          <Col md={3}>
            <Button
              bsPrefix="custom-hamburger"
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-purple-600 bg-white p-2 text-sm font-semibold text-purple-600 shadow-md transition-all duration-200 hover:bg-purple-50 hover:shadow-lg"
              onClick={() => setShowModal(true)}
            >
              <FaPlus aria-hidden="true" /> Enroll Learners
            </Button>
          </Col>
        </Row>
      </div>

      <section className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3" aria-label="Enrollment summary">
        <div className="flex items-center gap-3 rounded-2xl border border-purple-100 bg-white/90 p-4 shadow-sm">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
            <FaUsers aria-hidden="true" />
          </span>
          <div>
            <p className="mb-0 text-2xl font-bold text-slate-900">{enrollments.length}</p>
            <p className="mb-0 text-sm text-slate-500">Total enrollments</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-purple-100 bg-white/90 p-4 shadow-sm">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <FaUsers aria-hidden="true" />
          </span>
          <div>
            <p className="mb-0 text-2xl font-bold text-slate-900">
              {enrollments.filter((enrollment) => ['assigned', 'in_progress'].includes(enrollment.status)).length}
            </p>
            <p className="mb-0 text-sm text-slate-500">Active learners</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-purple-100 bg-white/90 p-4 shadow-sm">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <FaCheckCircle aria-hidden="true" />
          </span>
          <div>
            <p className="mb-0 text-2xl font-bold text-slate-900">
              {enrollments.filter((enrollment) => enrollment.status === 'completed').length}
            </p>
            <p className="mb-0 text-sm text-slate-500">Completed</p>
          </div>
        </div>
      </section>

      <div className="overflow-x-auto rounded-2xl bg-white shadow-2xl">
        <Table bordered responsive hover className="m-0 min-w-max align-middle sm:min-w-0">
          <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
            <tr>
              <th className="!bg-purple-600 !px-3 !py-3 !text-white">
                <span className="flex items-center gap-2"><FaLayerGroup aria-hidden="true" /> Type</span>
              </th>
              <th className="!bg-purple-600 !px-3 !py-3 !text-white">
                <span className="flex items-center gap-2"><FaUser aria-hidden="true" /> Target</span>
              </th>
              <th className="!bg-purple-600 !px-3 !py-3 !text-white">
                <span className="flex items-center gap-2"><FaSignal aria-hidden="true" /> Status</span>
              </th>
              <th className="!bg-purple-600 !px-3 !py-3 !text-white">
                <span className="flex items-center gap-2"><FaCalendarAlt aria-hidden="true" /> Enrolled At</span>
              </th>
              <th className="!bg-pink-600 !px-3 !py-3 !text-white">
                <span className="flex items-center justify-center gap-2"><FaCog aria-hidden="true" /> Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {enrollments.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-5 text-gray-500">No enrollments yet.</td>
              </tr>
            ) : (
              enrollments.map((e) => (
                <tr key={e.id}>
                  <td className="px-3 text-capitalize">{e.enrollment_type}</td>
                  <td className="px-3">
                    {e.enrollment_type === 'user' && (
                      <span>{e.user ? `${e.user.first_name} ${e.user.last_name}` : `User #${e.user_id}`}</span>
                    )}
                    {e.enrollment_type === 'group' && <span>{e.group?.name || `Group #${e.group_id}`}</span>}
                    {e.enrollment_type === 'organization' && (
                      <span>{e.organization?.name || `Org #${e.organization_id}`}</span>
                    )}
                    {e.user?.email && <div className="text-xs text-gray-500">{e.user.email}</div>}
                  </td>
                  <td className="px-3">
                    <Badge bg={
                      e.status === 'completed' ? 'success'
                        : e.status === 'in_progress' ? 'primary'
                          : e.status === 'expired' ? 'danger' : 'secondary'
                    }>
                      {e.status}
                    </Badge>
                  </td>
                  <td className="px-3">{e.enrolled_at ? new Date(e.enrolled_at).toLocaleString() : '—'}</td>
                  <td className="px-3 text-center">
                    <Button
                      bsPrefix="custom-hamburger"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-300 bg-white p-0 text-red-600 transition-colors hover:bg-red-50"
                      onClick={() => setEnrollmentToRemove(e)}
                      aria-label="Remove enrollment"
                      title="Remove enrollment"
                    >
                      <FaTrash className="h-3.5 w-3.5" aria-hidden="true" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" className="border-0 bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-3 text-white modal-header">
          <Modal.Title className="text-xl font-bold">Enroll Learners / Groups / Organizations</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-4">
          {journey?.status !== 1 && (
            <Alert variant="info" className="py-2">
              This journey will be activated automatically when learners are enrolled.
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Enrollment Target</Form.Label>
            <Form.Select
              value={form.enrollment_type}
              onChange={(e) => setForm((p) => ({ ...p, enrollment_type: e.target.value }))}
              aria-label="Enrollment type"
            >
              <option value="user">Individual Learners</option>
              <option value="group">Group</option>
              <option value="organization">Organization</option>
            </Form.Select>
          </Form.Group>

          {form.enrollment_type === 'user' && (
            <div className="border rounded-xl p-3 max-h-64 overflow-auto bg-light">
              {users.length === 0 ? (
                <p className="mb-0 text-muted text-center py-3">No active learners available.</p>
              ) : (
                users.map((u) => (
                  <Form.Check
                    key={u.id}
                    type="checkbox"
                    id={`user-${u.id}`}
                    className="mb-2"
                    label={`${u.first_name} ${u.last_name} (${u.email})`}
                    checked={form.user_ids.includes(u.id)}
                    onChange={() => toggleUser(u.id)}
                  />
                ))
              )}
            </div>
          )}

          {form.enrollment_type === 'group' && (
            <Form.Select
              value={form.group_id}
              onChange={(e) => setForm((p) => ({ ...p, group_id: e.target.value }))}
              aria-label="Select group"
            >
              <option value="">Select group...</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </Form.Select>
          )}

          {form.enrollment_type === 'organization' && (
            <Form.Select
              value={form.organization_id}
              onChange={(e) => setForm((p) => ({ ...p, organization_id: e.target.value }))}
              aria-label="Select organization"
            >
              <option value="">Select organization...</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </Form.Select>
          )}

          <Form.Group className="mt-3">
            <Form.Label>Expires At (optional)</Form.Label>
            <Form.Control
              type="datetime-local"
              value={form.expires_at}
              onChange={(e) => setForm((p) => ({ ...p, expires_at: e.target.value }))}
              aria-label="Expires at"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0 bg-gray-100 p-4">
          <Row className="w-100 g-3">
            <Col sm={6}>
              <Button bsPrefix="custom-hamburger" className="w-full rounded-xl border-2 border-gray-300 bg-gray-100 p-2 font-semibold text-gray-700 shadow-md hover:bg-gray-200" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
            </Col>
            <Col sm={6}>
              <Button bsPrefix="custom-hamburger" className="w-full rounded-xl border border-purple-600 bg-white p-2 font-semibold text-purple-600 shadow-md hover:bg-purple-50" onClick={handleEnroll} disabled={saving || !canEnroll}>
                {saving ? <Spinner size="sm" animation="border" /> : 'Enroll'}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(enrollmentToRemove)} onHide={() => !removing && setEnrollmentToRemove(null)} centered>
        <Modal.Header closeButton={!removing} closeVariant="white" className="border-0 bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-3 text-white modal-header">
          <Modal.Title className="text-xl font-bold">Confirm Removal</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 py-10 text-center">
          <FaTrash className="mx-auto mb-4 text-5xl text-red-500" aria-hidden="true" />
          <h5 className="mb-2 font-bold text-gray-800">Remove Enrollment</h5>
          <p className="mb-0 text-gray-600">Are you sure you want to remove this learner or target from the journey?</p>
        </Modal.Body>
        <Modal.Footer className="border-0 bg-gray-100 p-4">
          <Row className="w-100 g-3">
            <Col sm={6}>
              <Button bsPrefix="custom-hamburger" className="w-full rounded-xl border-2 border-gray-300 bg-gray-100 p-2 font-semibold text-gray-700 shadow-md hover:bg-gray-200" disabled={removing} onClick={() => setEnrollmentToRemove(null)}>
                Cancel
              </Button>
            </Col>
            <Col sm={6}>
              <Button bsPrefix="custom-hamburger" className="w-full rounded-xl border border-red-500 bg-white p-2 font-semibold text-red-600 shadow-md hover:bg-red-50" disabled={removing} onClick={handleRemove}>
                {removing ? <Spinner size="sm" animation="border" /> : 'Remove Enrollment'}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LearningJourneyEnrollments;
